/*  Proposta C — Consumi
    Lo stress test definitivo: KPI con sparkline, ranking multi-hotel,
    trend 12 mesi, heatmap, allerte. Se C regge qui, regge ovunque.
*/

import React from 'react';
import { useV2Fonts } from './_shared';
import { CShell, C, CBadge, CCard } from './_cShell';
import {
  Zap, Droplets, Flame, TrendingUp, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Sparkles
} from 'lucide-react';

const HOTELS = [
  { code: 'GAL', name: 'Galleria', kwh: 32.4, water: 0.81, smc: 4.6, eff: 67, all_electric: false, trend: 'up' },
  { code: 'ARC', name: 'Arcimboldi', kwh: 29.1, water: 0.74, smc: 3.9, eff: 71, all_electric: false, trend: 'up' },
  { code: 'MNZ', name: 'Manzoni', kwh: 41.2, water: 1.12, smc: 6.1, eff: 58, all_electric: false, trend: 'down' },
  { code: 'KIR', name: 'Kira', kwh: 52.8, water: 0.78, smc: 0, eff: 63, all_electric: true, trend: 'up' },
  { code: 'DOR', name: 'Dorian', kwh: 36.5, water: 0.92, smc: 5.2, eff: 64, all_electric: false, trend: 'flat' },
];

const TREND_12M = [55, 58, 61, 59, 60, 62, 65, 64, 66, 67, 67, 67];

const ALERTS = [
  { hotel: 'MNZ', metric: 'kWh/cam', delta: '+34%', period: 'vs media 6m', severity: 'red' },
  { hotel: 'GAL', metric: 'm³/cam', delta: '+19%', period: 'vs media 6m', severity: 'amber' },
  { hotel: 'KIR', metric: 'kWh/cam', delta: '+11%', period: 'vs media 6m', severity: 'amber' },
];

// Heatmap consumi: 12 mesi × 5 hotel
const HEATMAP_DATA = HOTELS.map((h) => {
  const row = [];
  for (let m = 0; m < 12; m++) {
    const base = h.eff;
    const seasonal = Math.sin((m / 12) * Math.PI * 2) * 8;
    const noise = ((m * 13 + h.eff) % 100) / 100 * 10 - 5;
    row.push(Math.round(base + seasonal + noise));
  }
  return { hotel: h.code, values: row };
});

const heatColor = (v) => {
  if (v >= 70) return { bg: 'rgba(16,185,129,0.5)', border: '#10B981' };
  if (v >= 60) return { bg: 'rgba(16,185,129,0.25)', border: 'rgba(16,185,129,0.5)' };
  if (v >= 50) return { bg: 'rgba(245,158,11,0.3)', border: 'rgba(245,158,11,0.5)' };
  if (v >= 40) return { bg: 'rgba(245,158,11,0.5)', border: '#F59E0B' };
  return { bg: 'rgba(239,68,68,0.55)', border: '#EF4444' };
};

const Sparkline = ({ data, color = C.accent, height = 26, width = 80, glow = true }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} style={{ filter: glow ? `drop-shadow(0 0 4px ${color})` : 'none' }} />
    </svg>
  );
};

const ProposalCConsumption = () => {
  useV2Fonts();
  const ranked = [...HOTELS].sort((a, b) => b.eff - a.eff);
  const groupAvg = Math.round(HOTELS.reduce((s, h) => s + h.eff, 0) / HOTELS.length);

  return (
    <CShell hotel="Energy · Group View">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            MODULE_06 · ENERGY_CONSUMPTION
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: C.fontDisplay }}>Consumi Hotel</h1>
          <p className="text-xs mt-1" style={{ color: C.muted, fontFamily: C.fontMono }}>
            5 hotel monitorati · ultimi 12 mesi · 3 allerte attive · index gruppo {groupAvg}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CBadge color={C.success}>● 5/5 HOTELS REPORTING</CBadge>
          <button
            className="px-3 py-1.5 text-xs rounded-md"
            style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.fontMono }}
          >
            EXPORT_PDF
          </button>
        </div>
      </div>

      {/* Top KPI strip */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { icon: Sparkles, label: 'GROUP_EFF_INDEX', val: groupAvg, unit: '/100', delta: '+3pt', color: C.accent, spark: TREND_12M },
          { icon: Zap, label: 'AVG_KWH_PER_ROOM', val: 38.4, unit: '', delta: '−4.1%', color: C.success, spark: [42, 41, 40, 39, 39, 38, 38.4] },
          { icon: Droplets, label: 'AVG_M3_PER_ROOM', val: 0.87, unit: '', delta: '+2.3%', color: C.warning, spark: [0.80, 0.82, 0.83, 0.85, 0.86, 0.85, 0.87] },
          { icon: Flame, label: 'AVG_SMC_PER_ROOM', val: 3.96, unit: '', delta: '−12%', color: C.success, spark: [4.8, 4.6, 4.4, 4.2, 4.1, 4.0, 3.96] },
        ].map((k) => (
          <div
            key={k.label}
            className="rounded-lg p-4 relative overflow-hidden"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
                {k.label}
              </div>
              <k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />
            </div>
            <div className="flex items-end justify-between">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: C.fontDisplay }}>{k.val}</span>
                <span className="text-xs" style={{ color: C.muted, fontFamily: C.fontMono }}>{k.unit}</span>
              </div>
              <Sparkline data={k.spark} color={k.color} />
            </div>
            <div className="mt-2 text-[10px] flex items-center gap-1" style={{ color: k.color, fontFamily: C.fontMono }}>
              {k.delta.startsWith('+') && k.color === C.warning ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              Δ {k.delta} · 30d
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Ranking */}
        <CCard className="col-span-5" label="RANK_01 · HOTELS_BY_EFFICIENCY" title="Ranking efficienza">
          <div className="space-y-3">
            {ranked.map((h, idx) => (
              <div key={h.code} className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold tabular-nums"
                  style={{
                    background: idx === 0 ? 'rgba(16,185,129,0.15)' : C.surface2,
                    color: idx === 0 ? C.success : C.muted,
                    fontFamily: C.fontDisplay,
                  }}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold" style={{ fontFamily: C.fontDisplay }}>{h.name}</span>
                    <CBadge color={C.muted}>{h.code}</CBadge>
                    {h.all_electric && <CBadge color={C.accent}>ALL_ELECTRIC · k=80</CBadge>}
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: C.surface2 }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${h.eff}%`,
                        background: h.eff >= 65 ? C.success : h.eff >= 55 ? C.warning : C.danger,
                        boxShadow: `0 0 6px ${h.eff >= 65 ? C.success : h.eff >= 55 ? C.warning : C.danger}`,
                      }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-xl font-bold tabular-nums"
                    style={{
                      color: h.eff >= 65 ? C.success : h.eff >= 55 ? C.warning : C.danger,
                      fontFamily: C.fontDisplay,
                      textShadow: `0 0 12px ${h.eff >= 65 ? C.success : h.eff >= 55 ? C.warning : C.danger}55`,
                    }}
                  >
                    {h.eff}
                  </div>
                  <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
                    {h.trend === 'up' ? '↗' : h.trend === 'down' ? '↘' : '→'} {h.trend}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CCard>

        {/* 12 month trend */}
        <CCard className="col-span-7" label="CHART_02 · GROUP_EFF_TREND_12M" title="Trend efficienza gruppo">
          <div className="h-44 flex items-end gap-1.5">
            {TREND_12M.map((v, i) => {
              const max = Math.max(...TREND_12M);
              const min = Math.min(...TREND_12M);
              const h = ((v - min) / (max - min)) * 80 + 20;
              const isLast = i === 11;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="text-[9px] tabular-nums" style={{ color: isLast ? C.accent : C.mutedDim, fontFamily: C.fontMono }}>
                    {v}
                  </div>
                  <div
                    className="w-full rounded-t"
                    style={{
                      height: `${h}%`,
                      background: isLast
                        ? `linear-gradient(180deg, ${C.accent} 0%, rgba(59,130,246,0.3) 100%)`
                        : 'rgba(59,130,246,0.35)',
                      boxShadow: isLast ? `0 0 16px ${C.accentGlow}` : 'none',
                      minHeight: 4,
                    }}
                  />
                  <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-5 text-[10px]" style={{ color: C.muted, fontFamily: C.fontMono }}>
            <span>● MEDIA: {Math.round(TREND_12M.reduce((a,b)=>a+b,0)/12)}</span>
            <span style={{ color: C.success }}>● MAX: {Math.max(...TREND_12M)} (DIC)</span>
            <span style={{ color: C.warning }}>● MIN: {Math.min(...TREND_12M)} (GEN)</span>
            <span className="ml-auto">Δ 12m: +{Math.max(...TREND_12M) - Math.min(...TREND_12M)} pt</span>
          </div>
        </CCard>

        {/* Heatmap consumi 12m × 5 hotels */}
        <CCard className="col-span-8" label="HEATMAP_03 · HOTEL_x_MONTH" title="Mappa di calore · efficienza per mese">
          <div className="grid" style={{ gridTemplateColumns: '60px repeat(12, 1fr)', gap: 4 }}>
            <div />
            {['G', 'F', 'M', 'A', 'M', 'G', 'L', 'A', 'S', 'O', 'N', 'D'].map((m, i) => (
              <div key={i} className="text-center text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>{m}</div>
            ))}
            {HEATMAP_DATA.map((row) => (
              <React.Fragment key={row.hotel}>
                <div className="text-xs font-semibold flex items-center" style={{ color: C.muted, fontFamily: C.fontMono }}>
                  {row.hotel}
                </div>
                {row.values.map((v, i) => {
                  const hc = heatColor(v);
                  return (
                    <div
                      key={i}
                      className="aspect-square rounded flex items-center justify-center text-[10px] font-bold tabular-nums"
                      style={{
                        background: hc.bg,
                        border: `1px solid ${hc.border}`,
                        color: C.text,
                        fontFamily: C.fontMono,
                      }}
                    >
                      {v}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            <span>LEGEND:</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgba(239,68,68,0.55)' }} /> &lt;40 critico</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgba(245,158,11,0.5)' }} /> 40-60 attenzione</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgba(16,185,129,0.25)' }} /> 60-70 buono</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgba(16,185,129,0.5)' }} /> &gt;70 eccellente</span>
          </div>
        </CCard>

        {/* Alerts */}
        <CCard className="col-span-4" label="ALERTS_04 · THRESHOLDS_BREACHED" title="Allerte attive">
          <div className="space-y-3">
            {ALERTS.map((a, i) => (
              <div
                key={i}
                className="p-3 rounded-md"
                style={{
                  background: a.severity === 'red' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
                  border: `1px solid ${a.severity === 'red' ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
                }}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    className="w-4 h-4 mt-0.5"
                    style={{ color: a.severity === 'red' ? C.danger : C.warning }}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <span>{a.hotel}</span>
                      <CBadge color={a.severity === 'red' ? C.danger : C.warning}>
                        {a.severity === 'red' ? 'RED' : 'AMBER'}
                      </CBadge>
                    </div>
                    <div className="text-xs mt-1" style={{ color: C.muted }}>
                      <span style={{ fontFamily: C.fontMono }}>{a.metric}</span>{' '}
                      <span style={{ color: a.severity === 'red' ? C.danger : C.warning, fontWeight: 600 }}>{a.delta}</span>{' '}
                      <span style={{ color: C.mutedDim }}>{a.period}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              className="w-full py-2 text-xs rounded-md mt-2"
              style={{ background: C.surface2, border: `1px solid ${C.border}`, color: C.muted, fontFamily: C.fontMono }}
            >
              VIEW_ALL_THRESHOLDS →
            </button>
          </div>
        </CCard>
      </div>
    </CShell>
  );
};

export default ProposalCConsumption;
